import React from 'react';
import { ListSubheader } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useUserPermissions } from '../../../../apis/user-details';
import {
  AssignmentTurnedIn,
  BusinessCenter,
  CalendarMonthOutlined,
  Category,
  Clear,
  ComputerOutlined,
  Egg,
  EventBusyOutlined,
  Info,
  Inventory2Outlined as WarehouseIcon,
  List as ListIcon,
  LocalPizza,
  MenuBookOutlined as ProductMenuIcon,
  PersonOutline,
  Place,
  RedeemOutlined,
  StoreMallDirectory,
} from '@mui/icons-material';
import {
  CAMPAIGNS_READ,
  checkPermissions,
  hasAnyPermission,
  LOYALTY_READ,
  ORDERS_READ,
  PRODUCTS_READ,
  SHOPS_READ,
} from '../../../../shared/utils/permissions';
import PaymentExceptionsBadge from './PaymentExceptionsBadge';
import NavItem, { NavList } from './NavItem';

const ADMIN_PERMISSIONS = [LOYALTY_READ, CAMPAIGNS_READ, SHOPS_READ, PRODUCTS_READ, ORDERS_READ];

/** Whether the user may see any admin page; must cover every permission checked below. */
export const useHasAdminNav = (): boolean => {
  const { roles } = useUserPermissions();
  return hasAnyPermission(roles, ADMIN_PERMISSIONS);
};

const AdminNav: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { roles } = useUserPermissions();

  return (
    <NavList>
      {hasAnyPermission(roles, [LOYALTY_READ, CAMPAIGNS_READ, SHOPS_READ, ORDERS_READ]) && (
        <>
          <SectionHeader disableGutters>{t('nav.operations')}</SectionHeader>
          {checkPermissions(roles, [LOYALTY_READ]) && (
            <NavItem to="/customers" icon={<PersonOutline />} label={t('nav.customers')} onNavigate={onNavigate} />
          )}
          {checkPermissions(roles, [CAMPAIGNS_READ]) && (
            <NavItem to="/campaigns" icon={<RedeemOutlined />} label={t('nav.campaigns')} onNavigate={onNavigate} />
          )}
          {checkPermissions(roles, [SHOPS_READ]) && (
            <NavItem
              to="/holidays"
              icon={<CalendarMonthOutlined />}
              label={t('nav.holidays')}
              onNavigate={onNavigate}
            />
          )}
          {checkPermissions(roles, [SHOPS_READ]) && (
            <NavItem
              to="/global-exception-hours"
              icon={<EventBusyOutlined />}
              label={t('nav.globalExceptions')}
              onNavigate={onNavigate}
            />
          )}
          {checkPermissions(roles, [ORDERS_READ]) && (
            <NavItem to="/orders" icon={<ComputerOutlined />} label={t('nav.orders')} onNavigate={onNavigate} />
          )}
        </>
      )}
      {checkPermissions(roles, [PRODUCTS_READ]) && (
        <>
          <SectionHeader disableGutters>{t('nav.products')}</SectionHeader>
          <NavItem to="/products" icon={<LocalPizza />} label={t('nav.products')} onNavigate={onNavigate} />
          <NavItem
            to="/upcoming-products"
            icon={<AssignmentTurnedIn />}
            label={t('nav.upcomingProducts')}
            onNavigate={onNavigate}
          />
          <NavItem to="/ingredients" icon={<Egg />} label={t('nav.ingredients')} onNavigate={onNavigate} />
          <NavItem
            to="/ingredient-dosings"
            icon={<ListIcon />}
            label={t('nav.ingredientDosings')}
            onNavigate={onNavigate}
          />
          <NavItem
            to="/product-categories"
            icon={<Category />}
            label={t('nav.productCategories')}
            onNavigate={onNavigate}
          />
          <NavItem
            to="/warehouse-items"
            icon={<WarehouseIcon />}
            label={t('nav.warehouseItems')}
            onNavigate={onNavigate}
          />
          <NavItem
            to="/product-menus"
            icon={<ProductMenuIcon />}
            label={t('nav.productMenus')}
            onNavigate={onNavigate}
          />
          <NavItem to="/out-of-stock" icon={<Clear />} label={t('nav.outOfStockProducts')} onNavigate={onNavigate} />
        </>
      )}
      {checkPermissions(roles, [SHOPS_READ]) && (
        <>
          <SectionHeader disableGutters>{t('restaurants.list.title')}</SectionHeader>
          <NavItem to="/restaurants" icon={<StoreMallDirectory />} label={t('Restaurants')} onNavigate={onNavigate} />
          <NavItem to="/legal-entities" icon={<BusinessCenter />} label={t('Companies')} onNavigate={onNavigate} />
          <NavItem to="/zones" icon={<Category />} label={t('delivery zones')} onNavigate={onNavigate} />
          <NavItem to="/zones-v2" icon={<Place />} label={`${t('delivery zones')} v2`} onNavigate={onNavigate} />
          <NavItem
            to="/payment-methods-exceptions"
            icon={
              <PaymentExceptionsBadge>
                <Info />
              </PaymentExceptionsBadge>
            }
            label={t('Exceptions')}
            onNavigate={onNavigate}
          />
        </>
      )}
    </NavList>
  );
};

const SectionHeader = styled(ListSubheader)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  /* Pulls the header closer to its items than the list's 32px gap. */
  margin-bottom: ${({ theme }) => theme.spacing(-2.5)};
  line-height: 2.5;
  background-color: transparent;
  color: ${({ theme }) => theme.palette.common.white};
  opacity: 0.7;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  position: static;

  &:first-child {
    margin-top: 0;
  }
`;

export default AdminNav;
